import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const when = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function Diff({ res }) {
  const rows = Object.entries(res.per_collection);
  return (
    <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-xs text-stone-700 space-y-2" data-testid="restore-diff">
      <div className="font-semibold text-stone-900" data-testid="restore-totals">
        Would re-add <span data-testid="restore-add">{res.totals.add.toLocaleString()}</span> missing record{res.totals.add === 1 ? "" : "s"}, update {res.totals.change.toLocaleString()} where the archive is newer, leave {res.totals.same.toLocaleString()} as they are · deletes: 0
      </div>
      {rows.length ? (
        <ul className="grid grid-cols-2 gap-x-4 font-mono text-[11px]">{rows.map(([c, t]) => <li key={c}>{c}: +{t.add}{t.change ? ` ~${t.change}` : ""}</li>)}</ul>
      ) : <p className="text-stone-500">Nothing is missing — the live data already holds everything in this archive.</p>}
    </div>
  );
}

/** Restore from one archive: full or a single member, dry-run first, typed confirmation, never deletes. */
export function RestoreDialog({ run, onClose, onDone }) {
  const [mode, setMode] = useState("full");
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [dry, setDry] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (run) api.get(`/admin/backups/${run.id}/members`).then((r) => setMembers(r.data)).catch(() => {}); }, [run]);
  useEffect(() => { setDry(null); setConfirm(""); }, [mode, memberId]);
  if (!run) return null;
  const body = { mode, member_id: mode === "member" ? memberId : null };
  const dryRun = async () => {
    setBusy(true);
    try { setDry((await api.post(`/admin/backups/${run.id}/restore`, { ...body, dry_run: true })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doRestore = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/backups/${run.id}/restore`, { ...body, dry_run: false, confirm });
      toast.success(`Restored: ${r.data.totals.add.toLocaleString()} re-added, ${r.data.totals.change.toLocaleString()} updated, nothing deleted.`);
      onDone?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const canRun = mode === "full" || !!memberId;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Restore from {when(run.created_at)}</DialogTitle>
          <DialogDescription>{run.total.toLocaleString()} records in this archive. Restoring never deletes: missing records come back, newer live records win.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 text-xs">
          {[["full", "Everything"], ["member", "One member"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`restore-mode-${k}`} className={`px-3 py-1.5 rounded-full border font-semibold ${mode === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700"}`}>{l}</button>
          ))}
        </div>
        {mode === "member" && (
          <select value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="restore-member" className="w-full rounded-xl border border-stone-300 px-3 py-2 text-sm bg-white">
            <option value="">Pick a member in this archive…</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email} · {m.email}</option>)}
          </select>
        )}
        <button disabled={busy || !canRun} onClick={dryRun} data-testid="restore-dry-run" className="amber-btn text-xs py-1.5 self-start">{busy && !dry ? "Comparing…" : "Dry run — show what would change"}</button>
        {dry && <Diff res={dry} />}
        {dry && (dry.totals.add + dry.totals.change > 0) && (
          <div className="space-y-2">
            <label className="text-xs text-stone-600">Type <strong>RESTORE</strong> to confirm</label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="restore-confirm-input" className="w-full rounded-xl border border-stone-300 px-3 py-2 text-sm" placeholder="RESTORE" />
            <button disabled={busy || confirm.trim().toLowerCase() !== "restore"} onClick={doRestore} data-testid="restore-apply" className="w-full rounded-full bg-emerald-700 text-white text-sm font-semibold py-2 disabled:opacity-40">
              {busy ? "Restoring…" : `Restore ${dry.totals.add + dry.totals.change} record${dry.totals.add + dry.totals.change === 1 ? "" : "s"}`}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
